/**
 * Market data — spec §60.
 *
 * Ordered by what a reader arrives for. The naira rate leads because for a
 * Nigerian importer it is *the* number; world markets follow because they set
 * the cost of what lands at Apapa; and the index's own benchmarks come last
 * because they are the slowest-moving. The catalogue's coverage statistics —
 * which used to open the page — sit at the bottom, where a reader who wants
 * them will still find them and a reader who wants a rate is not made to scroll
 * past them.
 *
 * Every figure here is traceable, and the page says how many observations sit
 * behind each one. An "average price in Kano" computed from three listings is
 * an anecdote; presenting it with the same weight as one computed from four
 * hundred is the exact failure this product is meant to avoid, so the count is
 * shown next to the number rather than buried in a methodology note.
 *
 * The two data sources are independent queries and render independently. The
 * rate board must not wait on a price aggregate, and a failure in one must not
 * blank the other.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TickerTape, toTickerItems } from '@/components/data/marks'
import {
  BenchmarkTable, HeroRate, MarketBoard, RateTiles,
} from '@/components/data/markets'
import { ChartIcon, MapPinIcon } from '@/components/ui/icons'
import {
  Alert, EmptyState, Panel, SectionHeader, Skeleton, Stat,
} from '@/components/ui/primitives'
import { marketApi, priceApi, searchApi } from '@/services/endpoints'
import { formatMoney, formatNumber, formatRelative } from '@/utils/format'

export function MarketsPage() {
  const summary = useQuery({
    queryKey: ['market-summary'],
    queryFn: () => priceApi.marketSummary('NGN'),
  })
  const stats = useQuery({ queryKey: ['stats'], queryFn: searchApi.stats })
  const recent = useQuery({
    queryKey: ['prices', 'recent'],
    queryFn: () => priceApi.recent(1),
  })
  const rates = useQuery({ queryKey: ['market-rates'], queryFn: marketApi.rates })

  const data = summary.data
  const rateData = rates.data
  const usdRate = rateData?.fx.find((rate) => rate.symbol.startsWith('USD/'))
  const otherRates = rateData?.fx.filter((rate) => rate !== usdRate) ?? []

  return (
    <div>
      {/* -- Ticker ------------------------------------------------------- */}
      {rateData ? (
        <TickerTape items={toTickerItems(rateData.indices, rateData.fx)} />
      ) : null}

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
          Market data
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--text-secondary)]">
          Exchange rates, world markets, and price benchmarks computed from the
          supplier listings this index holds. Every figure shows when its source
          published it.
        </p>

        {/* -- The naira ---------------------------------------------------
            One hero figure per view. For a Nigerian buyer or importer, this is
            the number the rest of the page is read against. */}
        {usdRate ? (
          <section className="mt-8">
            <Panel raised className="p-6">
              <HeroRate rate={usdRate} />
            </Panel>
          </section>
        ) : null}

        {otherRates.length > 0 ? (
          <section className="mt-4">
            <RateTiles rates={otherRates} />
          </section>
        ) : null}

        {/* -- World markets ---------------------------------------------- */}
        {rateData && rateData.indices.length > 0 ? (
          <section className="mt-10">
            <SectionHeader
              title="World markets"
              description="Closing levels on the exchanges that set import prices, and Africa's largest markets. Each move is the exchange's own, against its previous session's close."
              className="mb-3"
            />
            <MarketBoard indices={rateData.indices} />
            <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">
              The Nigerian Exchange All-Share Index is not shown. No keyless feed
              carries it, and the nearest matching ticker belongs to a Nasdaq
              product — publishing that under a Nigerian label would be worse
              than the omission. Levels are session closes, not live prices.
            </p>
          </section>
        ) : null}

        {/* -- Our own benchmarks ------------------------------------------
            The only figures on this page we compute ourselves, and the only
            ones that carry a sample size. */}
        {rateData && rateData.index_benchmarks.length > 0 ? (
          <section className="mt-10">
            <SectionHeader
              title="Benchmarks from this index"
              description="Median published price per category, from the supplier listings held here. Quote-only offers are excluded and units are never mixed."
              className="mb-3"
            />
            <BenchmarkTable
              benchmarks={rateData.index_benchmarks}
              usdRate={usdRate}
            />
            <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">
              The dollar column converts at the published rate above, on the date
              shown — it is arithmetic over two sourced figures, not a quote. The
              offer count is there because a median over four listings and one
              over forty are different claims.
            </p>
          </section>
        ) : null}

        {rateData && rateData.commodities.length > 0 ? (
          <section className="mt-10">
            <SectionHeader
              title="Global commodity benchmarks"
              description="International reference prices, for context. Brent crude in dollars a barrel is not the price of diesel in Onitsha, so each row states its unit and how often the series is published."
              className="mb-3"
            />
            <RateTiles rates={rateData.commodities} />
          </section>
        ) : null}

        {rateData &&
        rateData.indices.length === 0 &&
        rateData.fx.length === 0 &&
        rateData.index_benchmarks.length === 0 ? (
          <div className="mt-8">
            <Alert tone="info" title="No reference rates recorded yet">
              Rates and market levels appear once <code>flask markets refresh</code>{' '}
              has run. Nothing is estimated to fill the gap.
            </Alert>
          </div>
        ) : null}

        {/* -- Catalogue coverage ------------------------------------------ */}
        {summary.isLoading ? (
          <div className="mt-12 space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-24" />
          </div>
        ) : !data || data.totals.observations === 0 ? (
          <div className="mt-12">
            <EmptyState
              icon={<ChartIcon size={20} />}
              title="Not enough price observations yet"
              description="Catalogue statistics appear once prices have been crawled, extracted and published. Nothing is estimated to fill the gap."
            />
          </div>
        ) : (
          <>
          {/* -- Totals ---------------------------------------------------- */}
          <section className="mt-8">
            <SectionHeader
              title="Coverage"
              description={`All figures in ${data.currency.code}.`}
              className="mb-3"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Panel>
                <Stat
                  label="Price observations"
                  value={formatNumber(data.totals.observations)}
                  hint="Current, comparable offers"
                />
              </Panel>
              <Panel>
                <Stat
                  label="Products priced"
                  value={formatNumber(data.totals.products)}
                />
              </Panel>
              <Panel>
                <Stat
                  label="Businesses quoting"
                  value={formatNumber(data.totals.businesses)}
                />
              </Panel>
              <Panel>
                <Stat
                  label="States covered"
                  value={`${stats.data?.states_covered ?? 0}`}
                  hint="of 37"
                />
              </Panel>
            </div>
          </section>

          {/* -- By state -------------------------------------------------- */}
          <section className="mt-10">
            <SectionHeader
              title="Average price by state"
              description={`Only states with at least ${data.minimum_observations_per_state} observations are shown.`}
              className="mb-3"
            />

            {data.by_state.length === 0 ? (
              <Alert tone="info">
                No state yet has {data.minimum_observations_per_state} or more price
                observations. Below that threshold an average is an anecdote, and publishing
                it as a market figure would be the most misleading thing on this page.
              </Alert>
            ) : (
              <Panel padded={false} className="overflow-x-auto">
                <table className="w-full min-w-[24rem] text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-default)] text-left">
                      <th className="px-4 pb-2 pt-4 field-label font-semibold">State</th>
                      <th className="px-4 pb-2 pt-4 field-label font-semibold text-right">
                        Average
                      </th>
                      <th className="px-4 pb-2 pt-4 field-label font-semibold text-right">
                        Observations
                      </th>
                      <th className="hidden px-4 pb-2 pt-4 field-label font-semibold sm:table-cell">
                        Relative
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-hairline">
                    {data.by_state.map((row) => {
                      const max = Math.max(
                        ...data.by_state.map((entry) => Number(entry.average)),
                      )
                      const width = (Number(row.average) / max) * 100
                      return (
                        <tr key={row.slug} className="hover:bg-[var(--surface-hover)]">
                          <td className="px-4 py-2.5">
                            <Link
                              to={`/search?state=${row.slug}`}
                              className="inline-flex items-center gap-1.5 text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                            >
                              <MapPinIcon size={12} className="text-[var(--text-tertiary)]" />
                              {row.state}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium tabular">
                            {formatMoney(row.average, data.currency.code)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs tabular text-[var(--text-tertiary)]">
                            {formatNumber(row.observations)}
                          </td>
                          <td className="hidden px-4 py-2.5 sm:table-cell">
                            <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                              <div
                                className="h-full rounded-full bg-[var(--accent)]"
                                style={{ width: `${Math.max(3, width)}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Panel>
            )}

            {/* An average across every product in a state is not a price index.
                Saying so is the difference between a data product and a chart. */}
            <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">
              These averages span every priced product in the index and every unit of sale.
              They indicate where observations cluster — they are not a cost-of-goods index,
              and two states are not directly comparable unless they carry the same product mix.
            </p>
          </section>
        </>
      )}

      {/* -- Recent ------------------------------------------------------- */}
      <section className="mt-10">
        <SectionHeader
          title="Latest observations"
          description="The freshest entries, so you can see how current the index is."
          className="mb-3"
        />
        <Panel padded={false}>
          {recent.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-8" />
              ))}
            </div>
          ) : (recent.data?.data.length ?? 0) === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-[var(--text-tertiary)]">
              No prices published yet.
            </p>
          ) : (
            <ul className="divide-hairline">
              {recent.data!.data.slice(0, 10).map((offer, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-hover)]"
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to={offer.product ? `/products/general/${offer.product.slug}` : '/products'}
                      className="block truncate text-sm text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                    >
                      {offer.product?.name}
                    </Link>
                    <p className="truncate text-2xs text-[var(--text-tertiary)]">
                      {offer.business?.name}
                      {offer.business?.location ? ` · ${offer.business.location}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-medium tabular">
                      {offer.price_type === 'quote'
                        ? 'On request'
                        : formatMoney(offer.amount, offer.currency ?? 'NGN')}
                    </p>
                    <p className="text-2xs text-[var(--text-tertiary)]">
                      {formatRelative(offer.observed_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        </section>
      </div>
    </div>
  )
}
